import * as PDFJS from 'pdfjs-dist'
import PDFWorker from 'pdfjs-dist/build/pdf.worker.min?url'
import { shallowRef, isRef, watch, onUnmounted, toRaw } from 'vue'

function configWorker(wokerSrc) {
  PDFJS.GlobalWorkerOptions.workerSrc = wokerSrc
}

function isBenignPdfError(error) {
  const msg = String(error?.message || error || '')
  return /transport destroyed|loading aborted|worker was terminated|reading 'getPage'/i.test(msg)
}

/**
 * Нормализует источник для pdfjs-dist ≥4/6:
 * getDocument() принимает только DocumentInitParameters ({ url } | { data }),
 * сырой Uint8Array/ArrayBuffer больше не поддерживается — без обёртки
 * data/url = null → пустой просмотрщик без ошибки в UI.
 * Также снимает Vue Proxy с TypedArray.
 *
 * @param {unknown} source
 * @returns {unknown}
 */
function normalizePdfSource(source) {
  const raw = toRaw(source)
  if (typeof raw === 'string' || raw instanceof URL) {
    return { url: raw }
  }
  if (raw instanceof Uint8Array || raw instanceof ArrayBuffer) {
    return { data: raw }
  }
  if (raw && typeof raw === 'object' && ArrayBuffer.isView(raw)) {
    return { data: raw.buffer }
  }
  return raw
}

export function usePDF(
  src,
  options = {
    onProgress: undefined,
    onPassword: undefined,
    onError: undefined,
    password: '',
  }
) {
  if (!PDFJS.GlobalWorkerOptions?.workerSrc) configWorker(PDFWorker)

  const pdf = shallowRef()
  const pdfDoc = shallowRef()
  const pages = shallowRef(0)
  const info = shallowRef({})

  let loadGeneration = 0
  let currentLoadingTask = null

  async function destroyCurrent() {
    const task = currentLoadingTask
    currentLoadingTask = null
    if (task) {
      try {
        await task.destroy()
      } catch (error) {
        console.error('[soykajs] не удалось уничтожить задачу загрузки PDF', error)
      }
    }
    const doc = pdfDoc.value
    pdfDoc.value = null
    pdf.value = undefined
    if (doc) {
      try {
        await doc.destroy()
      } catch (error) {
        console.error('[soykajs] не удалось уничтожить PDF-документ', error)
      }
    }
  }

  /** Закрытие окна: не ждём воркер, иначе крестик висит, пока getPage не упадёт. */
  function abandonDocument() {
    loadGeneration += 1
    const task = currentLoadingTask
    currentLoadingTask = null
    const doc = pdfDoc.value
    pdfDoc.value = null
    pdf.value = undefined
    pages.value = 0
    setTimeout(() => {
      Promise.resolve()
        .then(() => task?.destroy?.())
        .catch(error => {
          console.error(
            '[soykajs] не удалось уничтожить задачу загрузки PDF при закрытии',
            error
          )
        })
      Promise.resolve()
        .then(() => doc?.destroy?.())
        .catch(error => {
          console.error(
            '[soykajs] не удалось уничтожить PDF-документ при закрытии',
            error
          )
        })
    }, 0)
  }

  function processLoadingTask(source) {
    const generation = ++loadGeneration

    void (async () => {
      await destroyCurrent()
      if (generation !== loadGeneration) {
        return
      }

      const loadingTask = PDFJS.getDocument(normalizePdfSource(source))
      currentLoadingTask = loadingTask

      if (options.onProgress) {
        loadingTask.onProgress = options.onProgress
      }

      if (options.onPassword) {
        loadingTask.onPassword = options.onPassword
      } else if (options.password) {
        loadingTask.onPassword = (updatePassword, _) => {
          updatePassword(options.password ?? '')
        }
      } else {
        // Owner-only (пустой user-пароль): PDF.js всё равно зовёт onPassword.
        // Без обработчика promise не резолвится → вечный спиннер в просмотрщике.
        let emptyTried = false
        loadingTask.onPassword = (updatePassword, reason) => {
          const need =
            reason === PDFJS.PasswordResponses?.NEED_PASSWORD || reason === 1
          if (need && !emptyTried) {
            emptyTried = true
            updatePassword('')
            return
          }
          void loadingTask.destroy()
        }
      }

      try {
        const doc = await loadingTask.promise
        if (generation !== loadGeneration) {
          try {
            await doc.destroy()
          } catch (error) {
            console.error(
              '[soykajs] не удалось уничтожить устаревший PDF-документ',
              error
            )
          }
          return
        }

        pdfDoc.value = doc
        pdf.value = doc.loadingTask
        pages.value = doc.numPages

        const metadata = await doc.getMetadata()
        const attachments = await doc.getAttachments()
        const javascript = await doc.getJSActions()
        const outline = await doc.getOutline()

        if (generation !== loadGeneration) {
          return
        }

        info.value = {
          metadata,
          attachments,
          javascript,
          outline,
        }
      } catch (error) {
        if (generation !== loadGeneration || isBenignPdfError(error)) {
          return
        }
        if (typeof options.onError === 'function') {
          options.onError(error)
        }
      } finally {
        if (currentLoadingTask === loadingTask) {
          currentLoadingTask = null
        }
      }
    })()
  }

  async function getPDFDestination(destination) {
    const document = await pdf.value?.promise
    if (!document) return null

    const destArray = await getDestinationArray(document, destination)
    const destRef = await getDestinationRef(document, destArray)
    if (!destRef || !destArray) return null

    const pageIndex = await document.getPageIndex(destRef)

    const name = destArray[1].name
    const rest = destArray.slice(2)

    const location = isSpecLike(rest) ? getLocation(name, rest) : null

    return { pageIndex, location: location ?? { type: 'Fit', spec: [] } }
  }

  async function download(filename = 'filename') {
    if (!pdfDoc.value) throw new Error('Current PDFDocumentProxy have not loaded yet')
    const bytes = await pdfDoc.value?.saveDocument()
    const blobBytes = new Blob([bytes], { type: 'application/pdf' })
    const blobUrl = URL.createObjectURL(blobBytes)

    const anchorDownload = document.createElement('a')
    document.body.appendChild(anchorDownload)
    anchorDownload.href = blobUrl
    anchorDownload.download = filename
    anchorDownload.style.display = 'none'
    anchorDownload.click()

    setTimeout(() => {
      URL.revokeObjectURL(blobUrl)
      document.body.removeChild(anchorDownload)
    }, 10)
  }

  async function print(dpi = 150, filename = 'filename', prePrintCb = () => {}) {
    if (!pdfDoc.value) throw new Error('Current PDFDocumentProxy have not loaded yet')
    const bytes = await pdfDoc.value?.saveDocument()
    const savedLoadingTask = PDFJS.getDocument(normalizePdfSource(bytes.buffer))
    const savedDocument = await savedLoadingTask.promise

    const PRINT_UNITS = dpi / 72
    const CSS_UNITS = 96 / 72

    const iframe = await createIframe()
    const contentWindow = iframe.contentWindow
    const printDoc = contentWindow.document
    printDoc.title = filename
    addStylesToIframe(contentWindow)

    const pagesNumbers = [...Array(savedDocument.numPages).keys()].map(val => val + 1)

    for (const pageNumber of pagesNumbers) {
      const pageToPrint = await savedDocument.getPage(pageNumber)
      // Масштаб в viewport, без отдельного transform: поворот и CropBox
      // страницы остаются равномерными. Чужой transform плюс размер
      // первой страницы сплющивает текст после склейки файлов разного формата.
      const viewport = pageToPrint.getViewport({ scale: PRINT_UNITS })
      const cssWidth = (viewport.width / PRINT_UNITS) * CSS_UNITS
      const cssHeight = (viewport.height / PRINT_UNITS) * CSS_UNITS

      const canvas = document.createElement('canvas')
      canvas.width = Math.floor(viewport.width)
      canvas.height = Math.floor(viewport.height)

      await pageToPrint.render({
        canvasContext: canvas.getContext('2d'),
        intent: 'print',
        viewport,
      }).promise

      prePrintCb(canvas)

      // Холст создаём в документе iframe. cloneNode из другого документа
      // иногда сбрасывает размер буфера, и drawImage растягивает страницу.
      const pageCanvas = printDoc.createElement('canvas')
      pageCanvas.width = canvas.width
      pageCanvas.height = canvas.height
      pageCanvas.style.width = `${cssWidth}px`
      pageCanvas.style.height = `${cssHeight}px`
      printDoc.body.appendChild(pageCanvas)
      pageCanvas.getContext('2d')?.drawImage(canvas, 0, 0)
    }

    contentWindow.focus()
    await new Promise(resolve => {
      const done = () => {
        contentWindow.removeEventListener('afterprint', done)
        resolve()
      }
      contentWindow.addEventListener('afterprint', done)
      contentWindow.print()
    })
    iframe.remove()
  }

  if (isRef(src)) {
    if (src.value) processLoadingTask(src.value)
    watch(src, (next, prev) => {
      if (!next) {
        void destroyCurrent()
        pages.value = 0
        return
      }
      // Тот же ArrayBuffer/Uint8Array — не перезагружаем (иначе Transport destroyed).
      if (next === prev) {
        return
      }
      processLoadingTask(next)
    })
  } else {
    if (src) processLoadingTask(src)
  }

  // После дочерних VuePDF. destroy откладываем: синхронный обрыв
  // страницы даёт reading 'getPage' и держит закрытие просмотрщика.
  onUnmounted(() => {
    abandonDocument()
  })

  return {
    pdf,
    pages,
    info,
    print,
    download,
    getPDFDestination,
  }
}

async function createIframe() {
  return new Promise(resolve => {
    const iframe = document.createElement('iframe')

    // Не display:none и не 0×0: печать снимает вёрстку с бокса iframe.
    // За экраном, с ненулевым размером.
    iframe.style.cssText =
      'position: fixed; left: -10000px; top: 0; width: 1200px; height: 1600px; border: 0;'

    iframe.onload = function () {
      resolve(iframe)
    }
    document.body.appendChild(iframe)
  })
}

/**
 * Стили для печати. Колонтитулы (URL, дата, номер страницы) добавляет браузер
 * в диалоге печати — отключить их кодом нельзя, только в настройках печати.
 *
 * Как отключить колонтитулы в браузере:
 * - Chrome / Edge: Ctrl+P → «Ещё настройки» / «Дополнительные параметры» →
 *   снять галочку «Колонтитулы» (Headers and footers).
 * - Firefox: Ctrl+P → «Параметры страницы…» → снять галочки с верхнего и нижнего колонтитула.
 */
function addStylesToIframe(content) {
  const style = content.document.createElement('style')
  // Размер каждой страницы задаётся на её canvas (свой CropBox).
  // Общий size с первой страницы растягивает остальные неравномерно.
  style.textContent = `
    @page {
      margin: 0;
    }
    html, body {
      margin: 0;
      padding: 0;
    }
    canvas {
      display: block;
      page-break-after: always;
      page-break-before: avoid;
      page-break-inside: avoid;
      break-after: page;
      break-inside: avoid;
    }
    canvas:last-child {
      page-break-after: auto;
      break-after: auto;
    }
  `
  content.document.head.appendChild(style)
}
